import * as React from 'react';
import { Button, Input, MessageBar, MessageBarBody } from '@fluentui/react-components';
import { CopyRegular, MailRegular } from '@fluentui/react-icons';
import { createUploadLink, type IUploadLinkInfo, listUploadLinks, revokeUploadLink } from '../api';
import { errorText, useApp } from '../app/context';
import { takeResume } from '../auth';
import { listChildren } from '../graphx';
import { askForMail, createDraft } from '../outlook';
import { type IApplicationView, type IItemView, publicLabel, uploadSlots } from '../records/model';
import type { ITabData } from '../records/requests';
import { uploadEmail } from '../records/uploadEmail';
import { useLoad } from '../records/useLoad';

const FROM_CLIENT: string = 'From client';

/** The application's "From client" folder, where the client's uploads land. */
async function fromClientFolder(app: ReturnType<typeof useApp>, driveId: string, a: IApplicationView): Promise<string> {
  if (!a.folderId) {
    throw new Error('This application has no folder.');
  }
  const folder = (await listChildren(app.graph, driveId, a.folderId, 500)).filter(c => c.isFolder && c.name.toLowerCase() === FROM_CLIENT.toLowerCase())[0];
  if (!folder) {
    throw new Error(`There's no "${FROM_CLIENT}" folder in this application's folder. Create it there, then try again.`);
  }
  return folder.id;
}

/**
 * "Email the client an upload link": one Outlook draft with the file number, client, program, the
 * documents still needed, a new upload link and its end date (a restricted file's: the file number
 * and the link only). Items still "Needed" are marked Requested first, so the client's page shows
 * them. Staff read the draft and send it themselves; nothing is ever sent for them.
 */
export function EmailUploadLinkButton(props: { data: ITabData; primary?: boolean; onChanged: () => void }): React.ReactElement {
  const app = useApp();
  const d: ITabData = props.data;
  const a: IApplicationView = d.application;
  const [working, setWorking] = React.useState<string | undefined>(undefined);
  const [error, setError] = React.useState<string | undefined>(undefined);
  const [done, setDone] = React.useState<string | undefined>(undefined);
  const enabled: boolean = !!app.workspace.uploads?.enabled;
  const needed: IItemView[] = d.items.filter(i => i.status === 'Needed' || i.status === 'Requested');

  const send = React.useCallback(async (): Promise<void> => {
    setError(undefined);
    setDone(undefined);
    try {
      // Outlook first: if Microsoft needs the person, nothing has been made yet when the page leaves.
      setWorking('Opening Outlook…');
      const resume = { kind: 'uploadEmail', application: a.key };
      await askForMail(resume);
      setWorking('Marking the documents requested…');
      const batch = d.set.writer.batch();
      for (const i of needed.filter(x => x.status === 'Needed')) {
        await batch.update(d.set.checklist, i.rec, { Status: 'Requested', ChangedBy: app.account.username, ChangedOn: new Date().toISOString() }, i.label, `Marked "${i.label}" requested`);
      }
      setWorking('Making the upload link…');
      const link = await createUploadLink(app.workspace.driveId, a.key, await fromClientFolder(app, d.set.library.driveId, a), d.set.restricted);
      setWorking('Writing the draft…');
      const email = uploadEmail({
        restricted: d.set.restricted, fileNumber: d.client?.number, clientName: d.client?.name ?? a.title, program: d.program?.name ?? a.programId,
        intro: app.workspace.uploadIntro, people: d.people, items: needed, url: link.url, expires: link.expires
      });
      const web: string = await createDraft(d.client?.email, email.subject, email.html, resume);
      window.open(web, '_blank', 'noopener');
      setDone(`The draft is in your Outlook Drafts${d.client?.email ? ` to ${d.client.email}` : ''}. Read it and send it yourself.`);
      props.onChanged();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setWorking(undefined);
    }
  }, [app, a, d, needed, props]);

  // Back from Microsoft's permission page: finish the email that was being made.
  React.useEffect(() => {
    const r = takeResume('uploadEmail');
    if (r && r.application === a.key && enabled) {
      void send();
    }
    // Once, on arrival.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!enabled) {
    return <span className="small" style={{ fontSize: 14 }}>To email the client an upload link, turn on client upload links in <a href="#/settings">Settings</a>.</span>;
  }
  return (
    <div className="stack" style={{ gap: 8 }}>
      <Button appearance={props.primary ? 'primary' : 'secondary'} icon={<MailRegular />} disabled={!!working || !needed.length} onClick={() => { void send(); }} style={{ alignSelf: 'flex-start' }}>
        {working ?? 'Email the client an upload link'}
      </Button>
      {error && <MessageBar intent="error"><MessageBarBody>{error}</MessageBarBody></MessageBar>}
      {done && <MessageBar intent="info"><MessageBarBody>{done}</MessageBarBody></MessageBar>}
      {!d.client?.email && !done && <span className="small">The client has no email address in their record, so the draft will have no recipient.</span>}
    </div>
  );
}

/**
 * The client's upload link on the Requests tab: email it (the usual way), or copy a link to send
 * another way, and the links made so far. For a restricted file the client's page shows no firm or
 * document names, only numbered slots, so staff see which document each number stands for.
 */
export default function UploadLinkCard(props: { data: ITabData; onChanged: () => void }): React.ReactElement | null {
  const app = useApp();
  const d: ITabData = props.data;
  const a = d.application;
  const restricted: boolean = d.set.restricted;
  const requested: IItemView[] = d.items.filter(i => i.status === 'Requested');
  const [url, setUrl] = React.useState<string | undefined>(undefined);
  const [working, setWorking] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string | undefined>(undefined);
  const enabled: boolean = !!app.workspace.uploads?.enabled;
  const { data, reload } = useLoad(() => (enabled ? listUploadLinks(a.key).then(r => r.links) : Promise.resolve([] as IUploadLinkInfo[])), [a.key, enabled]);

  const create = async (): Promise<void> => {
    setWorking(true);
    setError(undefined);
    try {
      const r = await createUploadLink(app.workspace.driveId, a.key, await fromClientFolder(app, d.set.library.driveId, a), restricted);
      setUrl(r.url);
      reload();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setWorking(false);
    }
  };

  const revoke = async (nonce: string): Promise<void> => {
    setError(undefined);
    try {
      await revokeUploadLink(nonce);
      reload();
    } catch (e) {
      setError(errorText(e));
    }
  };

  const live: IUploadLinkInfo[] = (data ?? []).filter(l => !l.revoked && Date.parse(l.expires) > Date.now());
  return (
    <section className="card" style={{ gap: 12 }}>
      <h2>Upload link for the client</h2>
      <span className="small" style={{ fontSize: 14 }}>
        {restricted
          ? 'This is a restricted file: the email has only the file number and the link, and the client\'s page shows no firm or document names, just numbered slots. Tell the client which document goes in which slot another way.'
          : 'One Outlook draft with the documents still needed and a link that works for 30 days. Files the client sends land in "From client", marked for you to check.'}
      </span>
      <EmailUploadLinkButton data={d} primary onChanged={() => { reload(); props.onChanged(); }} />
      {enabled && restricted && requested.length > 0 && (
        <ul className="steps-list" style={{ fontSize: 14 }}>
          {uploadSlots(requested).map(s => <li key={s.key}>{s.slot}: {publicLabel(s.label)}</li>)}
        </ul>
      )}
      {error && <MessageBar intent="error"><MessageBarBody>{error}</MessageBarBody></MessageBar>}
      {enabled && url && (
        <div style={{ display: 'flex', gap: 8 }}>
          <Input readOnly value={url} style={{ flexGrow: 1 }} aria-label="Upload link" />
          <Button icon={<CopyRegular />} onClick={() => { void navigator.clipboard.writeText(url).then(() => app.toast({ text: 'Link copied. Paste it into your message to the client.' })); }}>Copy</Button>
        </div>
      )}
      {enabled && url && <span className="small">Copy the link now: for safety it isn't shown again. You can always make a new one.</span>}
      {enabled && !url && (
        <span className="small">Sending it another way? <button type="button" className="link-btn" style={{ color: 'var(--blue)', fontSize: 13 }} disabled={working} onClick={() => { void create(); }}>{working ? 'Making a link…' : 'Copy a link instead'}</button> (the client's page shows the items marked Requested).</span>
      )}
      {live.length > 0 && (
        <table className="data-table">
          <thead><tr><th>Made</th><th>By</th><th>Works until</th><th>Files sent</th><th /></tr></thead>
          <tbody>{live.map(l => (
            <tr key={l.nonce}>
              <td>{new Date(l.createdAt).toLocaleDateString()}</td><td>{l.createdBy}</td><td>{new Date(l.expires).toLocaleDateString()}</td><td>{l.uploads}</td>
              <td><Button size="small" onClick={() => { void revoke(l.nonce); }}>Stop it</Button></td>
            </tr>
          ))}</tbody>
        </table>
      )}
    </section>
  );
}
